import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqData = [
  {
    question: "What is IntelliShift AI?",
    answer: "IntelliShift AI is a cutting-edge artificial intelligence platform that helps businesses automate tasks, gain insights, and enhance productivity through advanced machine learning algorithms and natural language processing."
  },
  {
    question: "How does the pricing work?",
    answer: "We offer flexible pricing plans designed to accommodate businesses of all sizes. Our pricing is transparent with no hidden fees, and you can upgrade, downgrade, or cancel your subscription at any time."
  },
  {
    question: "Can I try IntelliShift before committing?",
    answer: "Yes! We offer a 14-day free trial that gives you full access to all features. No credit card is required to start your trial, and you can explore all the capabilities of our platform risk-free."
  },
  {
    question: "What kind of support do you offer?",
    answer: "We provide 24/7 customer support through multiple channels including live chat, email, and phone. Our dedicated support team is always ready to help you with any questions or technical issues you may encounter."
  },
  {
    question: "Is my data secure with IntelliShift?",
    answer: "Absolutely. We implement industry-leading security measures including end-to-end encryption, regular security audits, and compliance with major data protection regulations. Your data's security is our top priority."
  },
  {
    question: "How does the integration process work?",
    answer: "Our platform offers seamless integration with popular tools and services. The process is straightforward with detailed documentation and API support. Our team is also available to assist with custom integration requirements."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="border-b border-[#1b16471a] dark:border-[#ffffff1a] last:border-none"
    >
      <button
        className="flex justify-between items-center w-full py-8 px-6 text-left hover:bg-[#1b164706] dark:hover:bg-[#ffffff06] transition-colors"
        onClick={onClick}
      >
        <span className="text-lg font-semibold mr-8 text-textDark dark:text-gray-100">{question}</span>
        <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border border-[#1b16471a] dark:border-[#ffffff1a] bg-[#1b164706] dark:bg-[#ffffff06]">
          <motion.svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <path
              d="M6 11V1M1 6H11"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </motion.svg>
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-8 px-6 text-gray-600 dark:text-gray-400 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="relative py-20 lg:py-32">
      {/* Minimal background design */}
      <div className="absolute inset-0">
        {/* Subtle dot pattern */}
        <div className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle at center, #6366f1 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(circle at center, black, transparent 70%)',
          }}
        />

        {/* Minimal glow accent */}
        <div 
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(168, 85, 247, 0.2) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="relative container">
        <div className="max-w-3xl mx-auto mb-20 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="h2 mb-4"
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="body-2 text-n-4"
          >
            Everything you need to know about IntelliShift AI. Can't find the answer you're looking for?{' '}
            <a href="#" className="text-indigo-500 hover:text-indigo-400">
              Contact our support
            </a>
          </motion.p>
        </div>

        <div className="relative max-w-3xl mx-auto backdrop-blur-sm rounded-2xl p-2">
          <div className="relative rounded-xl overflow-hidden">
            {/* FAQ Items container with glass effect */}
            <div className="relative bg-[#ffffff06] backdrop-blur-sm rounded-xl divide-y divide-[#ffffff0f]">
              {faqData.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={index === openIndex}
                  onClick={() => setOpenIndex(index === openIndex ? -1 : index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
